import axios from 'axios';
import {
  SpecialCaseSolvingSuccessResults,
  ConversationMessagesResponse,
  ConversationType,
  FlagId,
  Folder,
  FolderMessageContent,
  FolderMessagesResponse,
  FoldersUnreadMessagesIndicators,
  Message,
  MonitId,
  Priority,
} from '../../models/communication.types';
import env from '../../utils/env';
import { AxiosResponse } from 'axios';
import {
  MessagesFilter,
  MessagesSortById,
  translateMessagesSortByIdToMessagesSortBy,
} from '../../components/Mailbox/MessagesList/MessagesList.types';
import { addParam, addParamsList, downloadFile } from '../../utils/servicesHelper';
import { INotificationTypeId } from '../../components/MessageActions/NotificationType.component';

interface RequestBodyAttr {
  subject?: string | null;
  body: string;
  assessmentSessionId?: string | null;
  priority: Priority | null;
  file?: Blob | null;
  url?: string | null;
  recipients?: (string | undefined)[] | null;
  notificationType?: INotificationTypeId[] | null;
  recipientFunctionIdsFieldName?: string | null;
  examTasksGroupId?: string | null;
}

interface RequestBodyAttrWithFilters extends RequestBodyAttr {
  firstName?: string;
  lastName?: string;
  email?: string;
  pesel?: string;
  phone?: string;
  accountTypeFilter?: string;
  examCommitteeFilter?: string;
  accountStatus?: string;
  status?: string;
}

interface RequestBodyAttrSpecialCase {
  body: string;
  file?: Blob | null;
  url?: string;
  notificationType?: INotificationTypeId[] | null;
  specialCaseId: string;
  examTaskSolutionScoreId: string | undefined;
}

interface RequestBodyExamTaskGroupSolution {
  body: string;
  file?: Blob | null;
  url?: string;
  notificationType?: INotificationTypeId[] | null;
  examTaskGroupSolutionScoreId: string | undefined;
  subject: string;
  priority: Priority | null;
}

const getNotificationType = (requestBody: FormData, notificationType: INotificationTypeId[], query?: boolean) => {
  if (notificationType.includes(INotificationTypeId.EMAIL)) {
    requestBody.append(query ? 'EmailNotification' : 'Email', 'true');
  }
  if (notificationType.includes(INotificationTypeId.SMS)) {
    requestBody.append(query ? 'SmsNotification' : 'Sms', 'true');
  }
  if (notificationType.includes(INotificationTypeId.PUSH)) {
    requestBody.append(query ? 'PushNotification' : 'Push', 'true');
  }
};

export const getTextWithoutQuotAndSpace = (body: string) => {
  return body.replace(/"/g, '&quot;').replace(/'/g, '&rsquo;').replace(/ /g, '&#32;');
};

export const getTextWithQuotAndSpace = (body: string) => {
  return body.replace(/&quot;/g, '"').replace(/&rsquo;/g, "'").replace(/&#32;/g, ' ');
};

const getRequestBody = (attr: RequestBodyAttr, query?: boolean): FormData => {
  const requestBody = new FormData();
  if (attr.assessmentSessionId !== null) {
    requestBody.append('AssessmentSessionId', attr.assessmentSessionId || '');
  }
  if (attr.subject) {
    requestBody.append('Subject', getTextWithoutQuotAndSpace(attr.subject));
  }
  requestBody.append('Body', getTextWithoutQuotAndSpace(attr.body));
  if (attr.priority !== null && attr.priority !== undefined) {
    requestBody.append('PriorityId', attr.priority.toString());
  }
  if (attr.file !== null) {
    requestBody.append('Attachment', attr.file !== undefined ? attr.file : '');
  }
  if (attr.url !== null) {
    requestBody.append('Url', attr.url !== undefined ? attr.url : '');
  }
  if (attr.notificationType) {
    getNotificationType(requestBody, attr.notificationType, query);
  }
  if (attr.recipients) {
    attr.recipients.forEach((item: string | undefined) => {
      if (item !== undefined) {
        attr.recipientFunctionIdsFieldName && requestBody.append(attr.recipientFunctionIdsFieldName, item);
      }
    });
  }
  if (attr.examTasksGroupId !== null) {
    requestBody.append('ExamTasksGroupId', attr.examTasksGroupId !== undefined ? attr.examTasksGroupId : '');
  }
  return requestBody;
};

const getRequestBodyWithFilters = (attr: RequestBodyAttrWithFilters): FormData => {
  const requestBody = getRequestBody(attr, true);
  if (attr.firstName) {
    requestBody.append('firstName', attr.firstName);
  }
  if (attr.lastName) {
    requestBody.append('lastName', attr.lastName);
  }
  if (attr.email) {
    requestBody.append('Email', attr.email);
  }
  if (attr.pesel) {
    requestBody.append('Pesel', attr.pesel);
  }
  if (attr.phone) {
    requestBody.append('Phone', attr.phone.replace('+', '%2B'));
  }
  if (attr.accountTypeFilter) {
    requestBody.append('AccountTypes', attr.accountTypeFilter);
  }
  if (attr.examCommitteeFilter) {
    requestBody.append('ExamCommittees', attr.examCommitteeFilter);
  }
  if (attr.status && ['online', 'offline'].includes(attr.status)) {
    requestBody.append('isOnline', attr.status === 'online' ? 'true' : 'false');
  }
  if (attr.accountStatus) {
    requestBody.append(
      'EmailConfirmed',
      attr.accountStatus !== '' && attr.accountStatus !== 'Aktywne,Nieaktywne'
        ? attr.accountStatus === 'Aktywne'
          ? 'true'
          : 'false'
        : '',
    );
  }
  return requestBody;
};

const getRequestBodySpecialCase = (attr: RequestBodyAttrSpecialCase, query?: boolean): FormData => {
  const requestBody = new FormData();
  requestBody.append('Body', getTextWithoutQuotAndSpace(attr.body));
  if (attr.file !== null) {
    requestBody.append('Attachment', attr.file !== undefined ? attr.file : '');
  }
  if (attr.url !== null) {
    requestBody.append('Url', attr.url !== undefined ? attr.url : '');
  }
  if (attr.notificationType) {
    getNotificationType(requestBody, attr.notificationType, query);
  }
  if (attr.specialCaseId) {
    requestBody.append('SpecialCaseId', attr.specialCaseId);
  }
  if (attr.examTaskSolutionScoreId) {
    requestBody.append('ExamTaskSolutionScoreId', attr.examTaskSolutionScoreId);
  }
  return requestBody;
};

const getRequestBodyExamTaskGroupSolution = (attr: RequestBodyExamTaskGroupSolution, query?: boolean): FormData => {
  const requestBody = new FormData();
  requestBody.append('Body', getTextWithoutQuotAndSpace(attr.body));
  requestBody.append('Subject', getTextWithoutQuotAndSpace(attr.subject));
  if (attr.file !== null) {
    requestBody.append('Attachment', attr.file !== undefined ? attr.file : '');
  }
  if (attr.url !== null) {
    requestBody.append('Url', attr.url !== undefined ? attr.url : '');
  }
  if (attr.notificationType) {
    getNotificationType(requestBody, attr.notificationType, query);
  }
  if (attr.examTaskGroupSolutionScoreId) {
    requestBody.append('ExamTaskGroupSolutionScoreId', attr.examTaskGroupSolutionScoreId);
  }
  if (attr.priority !== null && attr.priority !== undefined) {
    requestBody.append('PriorityId', attr.priority.toString());
  }
  return requestBody;
};

export const sendMessage = async (
  subject: string,
  body: string,
  assessmentSessionId: string | undefined,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  recipients: (string | undefined)[],
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const requestBody = getRequestBody({
    subject,
    body,
    assessmentSessionId,
    priority,
    file,
    url,
    recipients,
    notificationType,
    recipientFunctionIdsFieldName: 'RecipientDeskIds',
    examTasksGroupId: null,
  });

  const response = await axios({
    method: 'POST',
    url: env.REACT_APP_COMMUNICATION__MESSAGE_URL,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    params: shouldGetMessageInfo
      ? {
          shouldGetMessageInfo: true,
        }
      : undefined,
  });
  return response.data;
};

export const sendMessageAdmin = async (
  subject: string,
  body: string,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  recipients: string[] | (string | undefined)[],
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const requestBody = getRequestBody({
    subject,
    body,
    assessmentSessionId: null,
    priority,
    file,
    url,
    recipients,
    notificationType,
    recipientFunctionIdsFieldName: 'Recipients',
    examTasksGroupId: null,
  });

  const response = await axios({
    method: 'POST',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/admin`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    params: shouldGetMessageInfo
      ? {
          shouldGetMessageInfo: true,
        }
      : undefined,
  });
  return response.data;
};

export const sendMessageAdminToAllChecked = async (
  subject: string,
  body: string,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  notificationType: INotificationTypeId[],
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
  status?: string,
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const requestBody = getRequestBodyWithFilters({
    subject,
    body,
    assessmentSessionId: null,
    priority,
    file,
    url,
    recipients: null,
    notificationType,
    recipientFunctionIdsFieldName: null,
    examTasksGroupId: null,
    firstName,
    lastName,
    email,
    pesel,
    phone,
    accountTypeFilter,
    examCommitteeFilter,
    accountStatus,
    status,
  });

  const response = await axios({
    method: 'POST',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/admin/query`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    params: shouldGetMessageInfo
      ? {
          shouldGetMessageInfo: true,
        }
      : undefined,
  });
  return response.data;
};

export const sendReply = async (
  messageId: string,
  body: string,
  priority: number,
  file: Blob | undefined,
  url: string | undefined,
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const requestBody = getRequestBody({
    subject: null,
    body,
    assessmentSessionId: null,
    priority,
    file,
    url,
    recipients: null,
    notificationType,
  });
  const response = await axios({
    method: 'PUT',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    params: shouldGetMessageInfo
      ? {
          shouldGetMessageInfo: true,
        }
      : undefined,
  });
  return response.data;
};

export const sendForward = async (
  messageId: string,
  body: string,
  priority: number,
  recipients: string[],
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const requestBody = getRequestBody({
    subject: null,
    body,
    assessmentSessionId: null,
    priority,
    recipients,
    notificationType,
    recipientFunctionIdsFieldName: 'Recipients',
    examTasksGroupId: null,
  });
  const response = await axios({
    method: 'POST',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/forward`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    params: shouldGetMessageInfo
      ? {
          shouldGetMessageInfo: true,
        }
      : undefined,
  });
  return response.data;
};

export const sendMessageHelpdesk = async (
  subject: string,
  body: string,
  examTasksGroupId: string | undefined,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const requestBody = getRequestBody({
    subject,
    body,
    assessmentSessionId: null,
    priority,
    file,
    url,
    recipients: null,
    notificationType: null,
    recipientFunctionIdsFieldName: null,
    examTasksGroupId,
  });

  const response = await axios({
    method: 'POST',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/helpdesk`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    params: shouldGetMessageInfo
      ? {
          shouldGetMessageInfo: true,
        }
      : undefined,
  });
  return response.data;
};

export const sendMessageSpecialCase = async (
  body: string,
  examTaskSolutionScoreId: string | undefined,
  specialCaseId: string,
  file?: Blob | null,
  url?: string,
  notificationType?: INotificationTypeId[] | null,
): Promise<Message> => {
  const requestBody = getRequestBodySpecialCase({
    body,
    file,
    url,
    notificationType,
    specialCaseId,
    examTaskSolutionScoreId,
  });

  const response = await axios({
    method: 'PUT',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/specialCase`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });
  return response.data;
};

export const sendMessageExamTaskGroupSolution = async (
  body: string,
  subject: string,
  priority: Priority,
  examTaskGroupSolutionScoreId: string | undefined,
  file?: Blob | null,
  url?: string,
  notificationType?: INotificationTypeId[] | null,
): Promise<Message> => {
  const requestBody = getRequestBodyExamTaskGroupSolution({
    body,
    file,
    url,
    notificationType,
    examTaskGroupSolutionScoreId,
    subject,
    priority,
  });

  const response = await axios({
    method: 'PUT',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/examTaskGroupSolution`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });
  return response.data;
};

export function getFolders(): Promise<AxiosResponse<FoldersUnreadMessagesIndicators>> {
  const response = axios.get(env.REACT_APP_COMMUNICATION__FOLDER_URL);
  return response;
}

const collectGetMessagesSortingParams = (
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
): string => {
  let params = '';
  if (sortByColumnId !== undefined) {
    params += addParam('sortBy', translateMessagesSortByIdToMessagesSortBy(sortByColumnId));
  }
  params += addParam('isDescending', isDescending ? 'true' : 'false');
  return params;
};

const collectGetMessagesParams = (
  filter: MessagesFilter,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
): string => {
  let params = '';
  params += addParam('assessmentSession', filter.assessmentSession, 3);
  params += addParam('sender', filter.sender, 3);
  params += addParam('recipient', filter.recipient, 3);
  params += addParam('subject', filter.subject, 3);
  params += addParam('sentDateFrom', filter.sentDateFrom);
  params += addParam('sentDateTo', filter.sentDateTo);
  params += addParamsList('flag', filter.flag);
  params += addParam('hasAttachment', filter.monit.includes(MonitId.hasAttachment) ? 'true' : '');
  params += addParam('hasLink', filter.monit.includes(MonitId.hasLink) ? 'true' : '');
  params += addParam('isRead', filter.isUnread ? 'false' : '');

  const priorities: Priority[] = [];
  if (filter.monit.includes(MonitId.highPriority)) {
    priorities.push(Priority.High);
  }
  if (filter.monit.includes(MonitId.mediumPriority)) {
    priorities.push(Priority.Medium);
  }
  params += addParamsList('priority', priorities);
  params += collectGetMessagesSortingParams(sortByColumnId, isDescending);
  return params;
};

export function getFolderMessages(
  folder: Folder,
  filter: MessagesFilter,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
  pageNumber: number,
  pageSize: number,
  includeFolderSummary: boolean,
  signal?: AbortSignal,
): Promise<AxiosResponse<FolderMessagesResponse>> {
  const params = collectGetMessagesParams(filter, sortByColumnId, isDescending);
  const response = axios.get(
    `${
      env.REACT_APP_COMMUNICATION__FOLDER_URL
    }/${folder}/message?page=${pageNumber}&pageSize=${pageSize}${params}&includeFolderSummary=${
      includeFolderSummary ? 'true' : 'false'
    }`,
    {
      signal: signal ?? undefined,
    },
  );
  return response;
}

export function getFolderMessagesSearchResults(
  folder: Folder,
  searchPhrase: string,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
  pageNumber: number,
  pageSize: number,
  includeFolderSummary: boolean,
  signal?: AbortSignal,
): Promise<AxiosResponse<FolderMessagesResponse>> {
  const params = collectGetMessagesSortingParams(sortByColumnId, isDescending);
  const response = axios.get(
    `${
      env.REACT_APP_COMMUNICATION__FOLDER_URL
    }/${folder}/message/search?page=${pageNumber}&pageSize=${pageSize}${params}&search=${searchPhrase}&includeFolderSummary=${
      includeFolderSummary ? 'true' : 'false'
    }`,
    {
      signal: signal ?? undefined,
    },
  );
  return response;
}

export function getConversationMessages(
  folder: Folder,
  conversationId: string,
  filter: MessagesFilter,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
  pageNumber: number,
  pageSize: number,
): Promise<AxiosResponse<ConversationMessagesResponse>> {
  const params = collectGetMessagesParams(filter, sortByColumnId, isDescending);
  const response = axios.get(
    `${env.REACT_APP_COMMUNICATION__FOLDER_URL}/${folder}/conversation/${conversationId}/message?page=${pageNumber}&pageSize=${pageSize}${params}`,
  );
  return response;
}

export function getMessageContent(messageId: string): Promise<AxiosResponse<FolderMessageContent>> {
  const response = axios.get(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}`);
  return response;
}

export const hasUnreadMessages = (priority: Priority): Promise<AxiosResponse<ConversationType>> => {
  const response = axios.get(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/hasUnreadMessages?priorityId=${priority}`);
  return response;
};

export const deleteMessage = (messageId: string): Promise<AxiosResponse> => {
  const response = axios.delete(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}`);
  return response;
};

export const setMessageFlag = (messageId: string, flagId: FlagId): Promise<AxiosResponse> => {
  const response = axios.put(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/flag/${flagId}`);
  return response;
};

export const removeMessageFlag = (messageId: string): Promise<AxiosResponse> => {
  const response = axios.delete(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/flag`);
  return response;
};

export function getAttachment(messageId: string, fileName: string): void {
  downloadFile(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/Attachment`, fileName);
}

export const closeSpecialCase = (conversationId: string): Promise<AxiosResponse<SpecialCaseSolvingSuccessResults>> => {
  const response = axios.patch(
    `${env.REACT_APP_COMMUNICATION__CONVERSATION_URL}/conversationId/${conversationId}/closeSpecialCase`,
  );
  return response;
};

export const closeHelpdesk = (conversationId: string): Promise<AxiosResponse> => {
  const response = axios.patch(
    `${env.REACT_APP_COMMUNICATION__CONVERSATION_URL}/conversationId/${conversationId}/closeHelpdeskRequest`,
  );
  return response;
};
